/***************************************************************
 * assignInspections.js
 *
 * We will export a function `assignInspectionsToInspectors` that
 * accepts:
 *   - selectedInspectorsDetails (array)
 *   - unscheduledInspections (array)
 *
 * Returns:
 *   - An array of objects, each shaped like:
 *     {
 *       assignedInspector: { ... all inspector fields ... },
 *       assignedInspections: [ array of matched inspections ],
 *     }
 *
 * Matching Criteria:
 *   - The inspector must have `districtCodes` that contains
 *     inspection.District.
 *   - Each inspector can have at most 9 inspections.
 *   - We assign them in order from highest to lowest priority
 *     (the unscheduledInspections list is already in descending
 *     priority order).
 ***************************************************************/

/**
 * assignInspectionsToInspectors
 * 
 * @param {Array} selectedInspectorsDetails
 *   e.g. [
 *     {
 *       "INSPECTOR_ID": "ACRAUS",
 *       "districtCodes": ["401", "402"],
 *       ...
 *     },
 *     {
 *       "INSPECTOR_ID": "AKANDASAMMY",
 *       "districtCodes": ["403", "404"],
 *       ...
 *     },
 *     ...
 *   ]
 * @param {Array} unscheduledInspections
 *   e.g. [
 *     {
 *       "INSPECTION_ID": "6776126",
 *       "District": "211",
 *       ...
 *     },
 *     ...
 *   ]
 *
 * @return {Array}
 *   e.g. [
 *     {
 *       assignedInspector: {
 *         INSPECTOR_ID: "ACRAUS",
 *         districtCodes: ["401", "402"],
 *         ...
 *       },
 *       assignedInspections: [
 *         { INSPECTION_ID: "....", District: "401", ... },
 *         ...
 *       ]
 *     },
 *     ...
 *   ]
 */
export function assignInspectionsToInspectors(selectedInspectorsDetails, unscheduledInspections) {
    // 1) Build a data structure to store assigned inspections for each inspector
    const inspectorAssignments = selectedInspectorsDetails.map((inspector) => ({
      assignedInspector: { ...inspector },
      assignedInspections: []
    }));

    // Keep track of which inspections have been assigned
    const assignedInspectionIds = new Set();
  
    // 2) Iterate over inspections in sorted order (already sorted from highest to lowest)
    let count = 0;
    for (const inspection of unscheduledInspections) {
      count++;
      // Skip if this inspection has already been assigned
      if (assignedInspectionIds.has(inspection.INSPECTION_ID)) {
        continue;
      }

      // find an inspector whose districtCodes includes inspection.District
      // AND who has fewer than 9 assignedInspections
      const foundInspector = inspectorAssignments.find((entry) => {
        const codes = Array.isArray(entry.assignedInspector.districtCodes)
          ? entry.assignedInspector.districtCodes
          : [];
        const canHandle = codes.includes(inspection.District);
  
        // also check capacity
        const currentCount = entry.assignedInspections.length;
        const hasCapacity = currentCount < 9;
  
        return canHandle && hasCapacity;
      });
  
      // if found, assign this inspection and any others at the same address
      if (foundInspector) {
        // First, assign the current inspection
        foundInspector.assignedInspections.push(inspection);
        assignedInspectionIds.add(inspection.INSPECTION_ID);

        // Then, look for other inspections at the same address
        const sameAddressInspections = unscheduledInspections.filter(otherInspection => 
          !assignedInspectionIds.has(otherInspection.INSPECTION_ID) && // Not already assigned
          otherInspection.INSPECTION_ID !== inspection.INSPECTION_ID && // Not the same inspection
          otherInspection.Street === inspection.Street && // Same street address
          foundInspector.assignedInspections.length < 9 // Inspector still has capacity
        );

        // Assign all inspections at the same address to this inspector
        for (const sameAddressInspection of sameAddressInspections) {
          foundInspector.assignedInspections.push(sameAddressInspection);
          assignedInspectionIds.add(sameAddressInspection.INSPECTION_ID);
        }
      }
    }
    console.log(count);
  
    // 3) Return the final array
    return inspectorAssignments;
  }
  